/**
 * Bracket generation and management for the elimination stage.
 */

import { SingleElimination } from 'tournament-pairings';
import type { CountryCode } from '@/data/countries/types';
import type { BracketMatch, AdvancingCountry } from './types';

/**
 * Get the next power of 2 that is >= n.
 */
function nextPowerOf2(n: number): number {
  if (n <= 1) return 1;
  return 2 ** Math.ceil(Math.log2(n));
}

/**
 * Get round name from round number.
 */
export function getRoundName(round: number, totalRounds: number, isThirdPlace?: boolean): string {
  if (isThirdPlace) {
    return 'Third Place';
  }

  const fromFinal = totalRounds - round + 1;
  switch (fromFinal) {
    case 1:
      return 'Final';
    case 2:
      return 'Semi-Finals';
    case 3:
      return 'Quarter-Finals';
    case 4:
      return 'Round of 16';
    default:
      return `Round ${round}`;
  }
}

/**
 * Generate a single-elimination bracket from advancing countries.
 * Uses tournament-pairings library for proper seeding (1v16, 2v15, etc.).
 */
export function generateBracket(advancingCountries: AdvancingCountry[]): BracketMatch[] {
  // Sort by points (higher = better seed)
  const seeded = [...advancingCountries].sort((a, b) => b.points - a.points);

  // Limit to 16 (or next power of 2 if fewer)
  const bracketSize = Math.min(16, nextPowerOf2(seeded.length));
  const participants = seeded.slice(0, bracketSize);

  // Generate pairings using tournament-pairings
  // Players are numbered 1-N, which we'll map to country codes
  const pairings = SingleElimination(participants.length, 1);

  // Map player numbers to country codes
  const playerMap = new Map<number, CountryCode>();
  participants.forEach((country, index) => {
    playerMap.set(index + 1, country.code);
  });

  // Convert pairings to our BracketMatch format
  const matches: BracketMatch[] = [];

  // Group pairings by round
  const byRound = new Map<number, typeof pairings>();
  for (const match of pairings) {
    const round = match.round;
    if (!byRound.has(round)) {
      byRound.set(round, []);
    }
    byRound.get(round)!.push(match);
  }

  // Convert each round
  for (const [round, roundMatches] of byRound) {
    for (const match of roundMatches) {
      // Get countries from player numbers (null if player is null or not in our map)
      const countryA = match.player1 !== null ? playerMap.get(match.player1 as number) : undefined;
      const countryB = match.player2 !== null ? playerMap.get(match.player2 as number) : undefined;

      matches.push({
        id: `match-r${round}-m${match.match}`,
        round,
        position: match.match,
        // If country is undefined, use empty string as placeholder
        countryA: (countryA ?? '') as CountryCode,
        countryB: (countryB ?? '') as CountryCode,
        winner: null,
        // Store winner routing info from the library
        winnerTo: match.win ? {
          round: match.win.round,
          match: match.win.match,
        } : undefined,
      });
    }
  }

  // Sort matches by round, then position
  matches.sort((a, b) => {
    if (a.round !== b.round) return a.round - b.round;
    return a.position - b.position;
  });

  // Dev logging for bracket generation
  if (import.meta.env.DEV) {
    const readyMatches = matches.filter(m => m.countryA !== '' && m.countryB !== '');
    const waitingMatches = matches.filter(m => m.countryA === '' || m.countryB === '');
    console.log('[Bracket] Generated bracket:', {
      participants: participants.length,
      totalMatches: matches.length,
      readyMatches: readyMatches.length,
      waitingMatches: waitingMatches.length,
      rounds: Math.max(...matches.map(m => m.round)),
      firstReadyMatch: readyMatches[0] ? {
        id: readyMatches[0].id,
        countryA: readyMatches[0].countryA,
        countryB: readyMatches[0].countryB,
      } : null,
    });
  }

  return matches;
}

/**
 * Get the index of the next undecided match that's ready to play.
 * A match is ready when both countries are populated (not empty placeholders).
 */
export function getNextMatchIndex(bracket: BracketMatch[]): number {
  return bracket.findIndex(
    match => match.winner === null && match.countryA !== '' && match.countryB !== ''
  );
}

/**
 * Record a match result and propagate winner to next match.
 * Also creates third-place match after both semi-finals are complete.
 */
export function recordMatchResult(
  bracket: BracketMatch[],
  matchId: string,
  winner: CountryCode
): BracketMatch[] {
  let updated = bracket.map(match => {
    if (match.id === matchId) {
      return { ...match, winner };
    }
    return match;
  });

  // Find the match we just decided
  const decidedMatch = updated.find(m => m.id === matchId);
  if (!decidedMatch) return updated;

  // Don't propagate if this is a third-place match
  if (decidedMatch.isThirdPlace) {
    return updated;
  }

  // Find the next match this winner should advance to using routing info
  const currentRound = decidedMatch.round;
  const totalRounds = Math.max(...updated.map(m => m.round));

  // Use the winnerTo routing from the library if available
  if (decidedMatch.winnerTo) {
    const nextMatch = updated.find(
      m => m.round === decidedMatch.winnerTo!.round &&
           m.position === decidedMatch.winnerTo!.match &&
           !m.isThirdPlace
    );

    if (nextMatch) {
      const matchIndex = updated.findIndex(m => m.id === nextMatch.id);
      if (matchIndex !== -1) {
        // Determine which slot to fill: prefer empty slot B if A is occupied, else A
        // This matches how the library structures byes (bye winner in slot A, feeder in slot B)
        const useSlotB = nextMatch.countryA !== '' && nextMatch.countryB === '';

        updated[matchIndex] = {
          ...updated[matchIndex],
          [useSlotB ? 'countryB' : 'countryA']: winner,
        };
      }
    }
  }

  // Check if we just completed a semi-final and need to create third-place match
  const semiFinalsRound = totalRounds - 1;
  if (currentRound === semiFinalsRound) {
    // Check if both semi-finals are complete
    const semiMatches = updated.filter(m => m.round === semiFinalsRound && !m.isThirdPlace);
    const bothSemisComplete = semiMatches.every(m => m.winner !== null);

    // Check if third-place match doesn't already exist
    const thirdPlaceExists = updated.some(m => m.isThirdPlace);

    if (bothSemisComplete && !thirdPlaceExists && semiMatches.length >= 2) {
      // Get the losers of both semi-finals
      const losers = semiMatches.map(m =>
        m.countryA === m.winner ? m.countryB : m.countryA
      );

      // Create third-place match
      const thirdPlaceMatch: BracketMatch = {
        id: 'match-third-place',
        round: totalRounds, // Same round as final
        position: 0, // Special position
        countryA: losers[0],
        countryB: losers[1],
        winner: null,
        isThirdPlace: true,
      };

      // Insert third-place match before the final
      const finalIndex = updated.findIndex(m => m.round === totalRounds && !m.isThirdPlace);
      if (finalIndex !== -1) {
        updated = [...updated.slice(0, finalIndex), thirdPlaceMatch, ...updated.slice(finalIndex)];
      } else {
        updated.push(thirdPlaceMatch);
      }
    }
  }

  return updated;
}

/**
 * Check if the bracket is complete (all real matches decided).
 * Ignores placeholder matches that haven't been populated yet.
 */
export function isBracketComplete(bracket: BracketMatch[]): boolean {
  // All matches with populated countries must have a winner
  const populatedMatches = bracket.filter(
    m => m.countryA !== '' && m.countryB !== ''
  );
  // And we need to have all rounds played (check that final exists and is decided)
  const totalRounds = Math.max(...bracket.map(m => m.round));
  const finalMatch = bracket.find(m => m.round === totalRounds && !m.isThirdPlace);
  const thirdPlaceMatch = bracket.find(m => m.isThirdPlace);

  // Complete when: final is decided AND (no 3rd place match OR 3rd place is decided)
  return (
    populatedMatches.every(m => m.winner !== null) &&
    finalMatch?.winner !== null &&
    (!thirdPlaceMatch || thirdPlaceMatch.winner !== null)
  );
}

/**
 * Get bracket rankings from completed bracket.
 * Returns countries in order from champion to last place.
 */
export function getBracketRankings(bracket: BracketMatch[]): CountryCode[] {
  if (!isBracketComplete(bracket)) return [];

  const rankings: CountryCode[] = [];
  const totalRounds = Math.max(...bracket.map(m => m.round));

  // Champion is the winner of the final match (not third-place)
  const finalMatch = bracket.find(m => m.round === totalRounds && !m.isThirdPlace);
  if (finalMatch && finalMatch.winner) {
    rankings.push(finalMatch.winner);

    // Runner-up is the loser of the final
    const runnerUp = finalMatch.countryA === finalMatch.winner
      ? finalMatch.countryB
      : finalMatch.countryA;
    if (runnerUp && runnerUp !== '') {
      rankings.push(runnerUp);
    }
  }

  // Third and fourth place from the third-place match
  const thirdPlaceMatch = bracket.find(m => m.isThirdPlace);
  if (thirdPlaceMatch && thirdPlaceMatch.winner) {
    // Third place is the winner of the third-place match
    rankings.push(thirdPlaceMatch.winner);

    // Fourth place is the loser
    const fourth = thirdPlaceMatch.countryA === thirdPlaceMatch.winner
      ? thirdPlaceMatch.countryB
      : thirdPlaceMatch.countryA;
    if (fourth && fourth !== '') {
      rankings.push(fourth);
    }
  }

  // Process each round from quarter-finals back (skip semis, they're handled above)
  for (let round = totalRounds - 2; round >= 1; round--) {
    const roundMatches = bracket.filter(m => m.round === round && !m.isThirdPlace);
    const losers = roundMatches
      .filter(m => m.winner && m.countryA && m.countryB && m.countryA !== '' && m.countryB !== '')
      .map(m => m.countryA === m.winner ? m.countryB : m.countryA);

    rankings.push(...losers);
  }

  // Dev logging to help debug ranking issues
  if (import.meta.env.DEV) {
    const allParticipants = new Set<string>();
    bracket.forEach(m => {
      if (m.countryA && m.countryA !== '') allParticipants.add(m.countryA);
      if (m.countryB && m.countryB !== '') allParticipants.add(m.countryB);
    });
    if (rankings.length !== allParticipants.size) {
      console.warn('[Bracket] Rankings mismatch:', {
        rankingsCount: rankings.length,
        participantsCount: allParticipants.size,
        rankings,
        participants: Array.from(allParticipants),
        missing: Array.from(allParticipants).filter(p => !rankings.includes(p as CountryCode)),
      });
    }
  }

  return rankings;
}

/**
 * Get the total number of playable matches in the bracket.
 * Excludes BYE matches and placeholder matches.
 */
export function getTotalMatches(bracket: BracketMatch[]): number {
  // Don't count auto-decided BYE matches or empty placeholders
  return bracket.filter(
    m => m.countryA !== '' && m.countryB !== '' && m.countryA !== m.countryB
  ).length;
}

/**
 * Get the number of decided matches (user-decided, not BYEs).
 */
export function getDecidedMatches(bracket: BracketMatch[]): number {
  return bracket.filter(
    m => m.winner !== null && m.countryA !== '' && m.countryB !== '' && m.countryA !== m.countryB
  ).length;
}

/**
 * Get matches in the current round.
 */
export function getMatchesInRound(bracket: BracketMatch[], round: number): BracketMatch[] {
  return bracket.filter(m => m.round === round && !m.isThirdPlace);
}

/**
 * Get decided matches in a specific round.
 */
export function getDecidedMatchesInRound(bracket: BracketMatch[], round: number): number {
  return bracket.filter(
    m => m.round === round && !m.isThirdPlace && m.winner !== null && m.countryA !== m.countryB
  ).length;
}
